import React, { useCallback, useEffect, useState } from "react";
import {
    ArrowRightOutlined,
    CalendarOutlined,
    ReloadOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import {
    Card,
    Col,
    Empty,
    Modal,
    Row,
    Select,
    Skeleton,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";
import { Client } from "../../core/api-client";
import {
    API_GET_DASHBOARD_BENCH_PEOPLE,
    API_GET_DASHBOARD_BENCH_FY_STAFF,
    API_GET_DASHBOARD_SUMMARY_COUNTS,
} from "../../shared/routes/private.routes";
import DashboardLoadError from "../dashboard/components/dashboard-load-error.component";
import { isFailedResponse } from "../dashboard/dashboard-response";
import "../../assets/styles/dashboard.less";
import MissingFieldsSummary from "../dashboard/components/missing-fields-summary.component";

const { Text, Title } = Typography;

type StaffContractAlert = {
    staffId: string;
    staffName: string;
    employeeId: string;
    clientName?: string;
    startDate?: string;
    contractEndDate: string;
    noticePeriodDays?: number | null;
    daysRemaining: number;
};

type BenchPerson = {
    staffId: string;
    staffName: string;
    employeeId: string;
    designation?: string | null;
    projectAllocationPercent: number;
    lopPercent: number;
    benchPercent: number;
};

type BenchFYPerson = BenchPerson;

type BenchMonthSummary = {
    month: string;
    label: string;
    total: number;
    people: BenchPerson[];
};

type BenchMonthFYSummary = {
    month: string;
    label: string;
    total: number;
    people: BenchFYPerson[];
};

type BenchMonthKey = "previousMonth" | "currentMonth" | "nextMonth";

type SummaryCounts = {
    staff: {
        total: number;
        active: number;
        newJoiners: number;
        inactive: number;
        noticePeriod: number;
        draft: number;
    };
    alerts: {
        interns: StaffContractAlert[];
        trainees: StaffContractAlert[];
        expiredStaffContracts: StaffContractAlert[];
        upcomingStaffContracts: StaffContractAlert[];
    };
};

type AlertType = "expired" | "upcoming" | "combined";
type AlertTableType = "staff" | "intern" | "trainee";

type DetailModalConfig =
    | {
        title: string;
        description: string;
        type: AlertType;
        tableType: AlertTableType;
        data: StaffContractAlert[];
    }
    | {
        title: string;
        description: string;
        tableType: "bench";
        data: BenchPerson[];
    }
    | {
        title: string;
        description: string;
        tableType: "benchFY";
        data: BenchFYPerson[];
    }
    | null;

const emptyCounts: SummaryCounts = {
    staff: {
        total: 0,
        active: 0,
        newJoiners: 0,
        inactive: 0,
        noticePeriod: 0,
        draft: 0,
    },
    alerts: {
        interns: [],
        trainees: [],
        expiredStaffContracts: [],
        upcomingStaffContracts: [],
    },
};

const staffFeaturedItems = [
    { label: "Active", key: "active", color: "success" },
    { label: "Notice Period", key: "noticePeriod", color: "warning" },
];

const staffCountItems = [
    { label: "New Joiners (Last 30 days)", key: "newJoiners", color: "processing" },
    { label: "Draft", key: "draft", color: "processing" },
    { label: "Inactive (Last 30 days)", key: "inactive", color: "default" },
];

const normalizeCounts = (data: any): SummaryCounts => ({
    staff: { ...emptyCounts.staff, ...(data?.staff || {}) },
    alerts: { ...emptyCounts.alerts, ...(data?.alerts || {}) },
});

const StaffDashboardPage: React.FC = () => {
    const [counts, setCounts] = useState<SummaryCounts>(emptyCounts);
    const [loading, setLoading] = useState(true);
    const [benchLoading, setBenchLoading] = useState(true);
    const [benchFYLoading, setBenchFYLoading] = useState(true);
    const [error, setError] = useState(false);
    const [missingFieldsRefresh, setMissingFieldsRefresh] = useState(0);


    const [benchMonths, setBenchMonths] = useState({
        previousMonth: getEmptyBenchMonth(getPreviousMonthValue()),
        currentMonth: getEmptyBenchMonth(getCurrentMonthValue()),
        nextMonth: getEmptyBenchMonth(getNextMonthValue()),
    });

    const [benchFYMonths, setBenchFYMonths] = useState({
        previousMonth: getEmptyBenchFYMonth(getPreviousMonthValue()),
        currentMonth: getEmptyBenchFYMonth(getCurrentMonthValue()),
        nextMonth: getEmptyBenchFYMonth(getNextMonthValue()),
    });

    const [selectedBenchMonth, setSelectedBenchMonth] =
        useState<BenchMonthKey>("currentMonth");
    const [selectedBenchFYMonth, setSelectedBenchFYMonth] =
        useState<BenchMonthKey>("currentMonth");
    const [detailModal, setDetailModal] = useState<DetailModalConfig>(null);

    const loadCounts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await Client.get(API_GET_DASHBOARD_SUMMARY_COUNTS);
            if (isFailedResponse(response)) {
                setError(true);
                return;
            }
            setCounts(normalizeCounts(response?.data?.data));
        } catch (error) {
            console.error("Failed to load staff dashboard summary.", error);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadBenchPeople = useCallback(async () => {
        const entries = getMonthEntries();
        setBenchLoading(true);
        try {
            const responses = await Promise.all(
                entries.map(({ month }) =>
                    Client.get(API_GET_DASHBOARD_BENCH_PEOPLE, { params: { month } })
                )
            );
            if (responses.some(isFailedResponse)) {
                setError(true);
                return;
            }

            setBenchMonths({
                previousMonth: responses[0]?.data?.data || getEmptyBenchMonth(entries[0].month),
                currentMonth: responses[1]?.data?.data || getEmptyBenchMonth(entries[1].month),
                nextMonth: responses[2]?.data?.data || getEmptyBenchMonth(entries[2].month),
            });
        } catch (error) {
            console.error("Failed to load unallocated staff.", error);
            setError(true);
        } finally {
            setBenchLoading(false);
        }
    }, []);

    const loadBenchFYPeople = useCallback(async () => {
        const entries = getMonthEntries();
        setBenchFYLoading(true);
        try {
            const responses = await Promise.all(
                entries.map(({ month }) =>
                    Client.get(API_GET_DASHBOARD_BENCH_FY_STAFF, { params: { month } })
                )
            );
            if (responses.some(isFailedResponse)) {
                setError(true);
                return;
            }

            setBenchFYMonths({
                previousMonth: responses[0]?.data?.data || getEmptyBenchFYMonth(entries[0].month),
                currentMonth: responses[1]?.data?.data || getEmptyBenchFYMonth(entries[1].month),
                nextMonth: responses[2]?.data?.data || getEmptyBenchFYMonth(entries[2].month),
            });
        } catch (error) {
            console.error("Failed to load bench staff.", error);
            setError(true);
        } finally {
            setBenchFYLoading(false);
        }
    }, []);

    useEffect(() => {
         setMissingFieldsRefresh((value) => value + 1);
    setError(false);
        void loadCounts();
        void loadBenchPeople();
        void loadBenchFYPeople();

    }, [loadCounts, loadBenchPeople, loadBenchFYPeople]);

    const selectedBench = benchMonths[selectedBenchMonth];
    const selectedBenchFY = benchFYMonths[selectedBenchFYMonth];

    const staffContractAlerts = [
        ...counts.alerts.expiredStaffContracts,
        ...counts.alerts.upcomingStaffContracts,
    ].sort(
        (a, b) =>
            a.contractEndDate.localeCompare(b.contractEndDate) ||
            a.staffName.localeCompare(b.staffName)
    );

    const refreshDashboard = () => {
        setError(false);
        void loadCounts();
        void loadBenchPeople();
        void loadBenchFYPeople();
    };

    return (
        <div className="app-page dashboard-page">
            <div className="app-page-header dashboard-page-header">
                <div>
                    <div className="app-page-title-wrap">
                        <span className="app-page-icon">
                            <TeamOutlined />
                        </span>
                        <Title level={1} className="app-page-title">
                            Staff Dashboard
                        </Title>
                    </div>
                    <p className="app-page-subtitle">Staff overview</p>
                </div>

                <button
                    className="dashboard-refresh-button"
                    type="button"
                    onClick={refreshDashboard}
                    disabled={loading || benchLoading || benchFYLoading}
                    aria-label="Refresh staff dashboard"
                >
                    <ReloadOutlined />
                    <span>Refresh</span>
                </button>
            </div>

            {error ? (
                <DashboardLoadError
                    message="Unable to load staff dashboard data."
                    onRetry={refreshDashboard}
                />
            ) : (
            <>
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <SummaryCard
                        title="Total Staff"
                        icon={<TeamOutlined />}
                        total={counts.staff.total}
                        featuredItems={staffFeaturedItems.map((item) => ({
                            ...item,
                            value: counts.staff[item.key as keyof SummaryCounts["staff"]],
                        }))}
                        items={staffCountItems.map((item) => ({
                            ...item,
                            value: counts.staff[item.key as keyof SummaryCounts["staff"]],
                        }))}
                        loading={loading}
                    />
                </Col>
            </Row>

            <Row
                gutter={[24, 16]}
                className="dashboard-alert-grid staff-dashboard-alert-grid"
            >
                <Col xs={24} xl={12}>
                    <BenchPeopleSection
                        month={selectedBench}
                        months={benchMonths}
                        selectedMonth={selectedBenchMonth}
                        onSelectedMonthChange={setSelectedBenchMonth}
                        loading={benchLoading}
                        onViewAll={(month) =>
                            setDetailModal({
                                title: `1. Unallocated Staff - ${month.label}`,
                                description: "Staff with less than 100% allocation",
                                tableType: "bench",
                                data: month.people,
                            })
                        }
                    />
                </Col>

                <Col xs={24} xl={12}>
                    <BenchFYStaffSection
                        month={selectedBenchFY}
                        months={benchFYMonths}
                        selectedMonth={selectedBenchFYMonth}
                        onSelectedMonthChange={setSelectedBenchFYMonth}
                        loading={benchFYLoading}
                        onViewAll={(month) =>
                            setDetailModal({
                                title: `2. Bench Staff List - ${month.label}`,
                                description: "Staff tagged under Bench",
                                tableType: "benchFY",
                                data: month.people,
                            })
                        }
                    />
                </Col>

                <Col xs={24} xl={12}>
                    <StaffContractAlertTable
                        title="3. Intern Expiry"
                        description="Intern contracts already expired or expiring in the next 30 days"
                        data={counts.alerts.interns}
                        type="combined"
                        period="Intern"
                        loading={loading}
                        onViewAll={() =>
                            setDetailModal({
                                title: "3. Intern Expiry",
                                description: "Intern contract details",
                                type: "combined",
                                tableType: "intern",
                                data: counts.alerts.interns,
                            })
                        }
                    />
                </Col>

                <Col xs={24} xl={12}>
                    <StaffContractAlertTable
                        title="4. Trainee Expiry"
                        description="Trainees expired in the last 45 days or expiring in the next 30 days"
                        data={counts.alerts.trainees}
                        type="combined"
                        period="Trainee"
                        loading={loading}
                        onViewAll={() =>
                            setDetailModal({
                                title: "4. Trainee Expiry",
                                description: "Trainee contract details",
                                type: "combined",
                                tableType: "trainee",
                                data: counts.alerts.trainees,
                            })
                        }
                    />
                </Col>

                <Col xs={24} xl={12}>
                    <StaffContractAlertTable
                        title="5. Staff Contract Expiry"
                        description="Expired contracts and contracts approaching the notice-period window"
                        data={staffContractAlerts}
                        type="combined"
                        loading={loading}
                        onViewAll={() =>
                            setDetailModal({
                                title: "5. Staff Contract Expiry",
                                description: "Staff contract details",
                                type: "combined",
                                tableType: "staff",
                                data: staffContractAlerts,
                            })
                        }
                    />
                </Col>
                 <Col xs={24}>
                          <MissingFieldsSummary refreshKey={missingFieldsRefresh} />
                        </Col>
            </Row>
            </>
            )}

            <DashboardDetailModal
                config={detailModal}
                onClose={() => setDetailModal(null)}
            />
        </div>
    );
};

type SummaryItem = {
    label: string;
    value: number;
    color: string;
};

function SummaryCard({
    title,
    icon,
    total,
    featuredItems = [],
    items,
    loading,
}: {
    title: string;
    icon: React.ReactNode;
    total: number;
    featuredItems?: SummaryItem[];
    items: SummaryItem[];
    loading: boolean;
}) {
    const statusItems = [...featuredItems, ...items];

    return (
        <Card className="dashboard-summary-card" variant="borderless">
            {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
                <>
                    <div className="dashboard-summary-top">
                        <div className="dashboard-summary-main">
                            <span className="dashboard-summary-icon">{icon}</span>
                            <Text className="dashboard-summary-title">{title}</Text>
                        </div>
                        <div className="dashboard-summary-total">{total}</div>
                    </div>
                    <div className="dashboard-status-grid">
                        {statusItems.map((item) => (
                            <div className="dashboard-status-item" key={item.label}>
                                <Space size={8}>
                                    <Tag color={item.color}>{item.label}</Tag>
                                </Space>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Card>
    );
}

function BenchPeopleSection({
    month,
    months,
    selectedMonth,
    onSelectedMonthChange,
    loading,
    onViewAll,
}: {
    month: BenchMonthSummary;
    months: Record<BenchMonthKey, BenchMonthSummary>;
    selectedMonth: BenchMonthKey;
    onSelectedMonthChange: (month: BenchMonthKey) => void;
    loading: boolean;
    onViewAll: (month: BenchMonthSummary) => void;
}) {
    return (
        <Card
            className="dashboard-bench-card"
            variant="borderless"
            title={
                <div className="dashboard-bench-header">
                    <div className="dashboard-bench-title">
                        <span>1. Unallocated Staff</span>
                        <span>Staff with less than 100% allocation</span>
                    </div>
                    <Select<BenchMonthKey>
                        className="dashboard-bench-month-select"
                        value={selectedMonth}
                        options={getMonthOptions(months, loading)}
                        onChange={onSelectedMonthChange}
                    />
                </div>
            }
        >
            <div className="dashboard-bench-table-wrap">
                <Table<BenchPerson>
                    size="small"
                    loading={loading}
                    rowKey="staffId"
                    dataSource={month.people.slice(0, 5)}
                    locale={{ emptyText: loading ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    pagination={false}
                    columns={getBenchColumns()}
                />
                <ViewAllFooter
                    count={month.people.length}
                    onViewAll={() => onViewAll(month)}
                />
            </div>
        </Card>
    );
}

function BenchFYStaffSection({
    month,
    months,
    selectedMonth,
    onSelectedMonthChange,
    loading,
    onViewAll,
}: {
    month: BenchMonthFYSummary;
    months: Record<BenchMonthKey, BenchMonthFYSummary>;
    selectedMonth: BenchMonthKey;
    onSelectedMonthChange: (month: BenchMonthKey) => void;
    loading: boolean;
    onViewAll: (month: BenchMonthFYSummary) => void;
}) {
    return (
        <Card
            className="dashboard-bench-card"
            variant="borderless"
            title={
                <div className="dashboard-bench-header">
                    <div className="dashboard-bench-title">
                        <span>2. Bench Staff List</span>
                        <span>Staff tagged under Bench</span>
                    </div>
                    <Select<BenchMonthKey>
                        className="dashboard-bench-month-select"
                        value={selectedMonth}
                        options={getMonthOptions(months, loading)}
                        onChange={onSelectedMonthChange}
                    />
                </div>
            }
        >
            <div className="dashboard-bench-table-wrap">
                <Table<BenchFYPerson>
                    size="small"
                    loading={loading}
                    rowKey="staffId"
                    dataSource={month.people.slice(0, 5)}
                    locale={{ emptyText: loading ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    pagination={false}
                    columns={getBenchColumns(true)}
                />
                <ViewAllFooter
                    count={month.people.length}
                    onViewAll={() => onViewAll(month)}
                />
            </div>
        </Card>
    );
}

function StaffContractAlertTable({
    title,
    description,
    data,
    type,
    loading,
    onViewAll,
    period,
}: {
    title: string;
    description: string;
    data: StaffContractAlert[];
    type: AlertType;
    loading: boolean;
    onViewAll: () => void;
    period?: "Intern" | "Trainee";
}) {
    return (
        <Card
            className="dashboard-alert-card"
            variant="borderless"
            title={
                <AlertTitle
                    title={title}
                    description={description}
                    count={loading ? undefined : data.length}
                    type={type}
                />
            }
        >
            <Table<StaffContractAlert>
                size="small"
                loading={loading}
                rowKey="staffId"
                dataSource={data.slice(0, 5)}
                locale={{ emptyText: loading ? null : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                pagination={false}
                columns={getStaffContractColumns(type, period)}
                scroll={{ x: 820 }}
            />
            <ViewAllFooter count={data.length} onViewAll={onViewAll} />
        </Card>
    );
}

function AlertTitle({
    title,
    description,
    count,
    type,
}: {
    title: string;
    description: string;
    count?: number;
    type: AlertType;
}) {
    return (
        <div className="dashboard-alert-title">
            <div className="dashboard-alert-title-main">
                <Space size={8}>
                    <CalendarOutlined />
                    <span>{title}</span>
                </Space>
                <span className="dashboard-alert-window">{description}</span>
            </div>
            {count !== undefined && <Tag color={type === "expired" ? "error" : "warning"}>{count}</Tag>}
        </div>
    );
}

function ViewAllFooter({
    count,
    onViewAll,
}: {
    count: number;
    onViewAll: () => void;
}) {
    if (!count) return null;

    return (
        <button
            className="dashboard-view-all-button"
            type="button"
            onClick={onViewAll}
        >
            <span>View all</span>
            <ArrowRightOutlined />
        </button>
    );
}

function DashboardDetailModal({
    config,
    onClose,
}: {
    config: DetailModalConfig;
    onClose: () => void;
}) {
    return (
        <Modal
            open={Boolean(config)}
            centered
            width="min(1080px, 92vw)"
            className="dashboard-detail-modal"
            footer={null}
            onCancel={onClose}
            title={
                config ? (
                    <div className="dashboard-detail-title">
                        <span>{config.title}</span>
                        <span>{config.description}</span>
                    </div>
                ) : null
            }
        >
            {config && (
                <div className="dashboard-detail-table-wrap">
                    {config.tableType === "bench" ||
                        config.tableType === "benchFY" ? (
                        <Table<BenchPerson>
                            size="small"
                            rowKey="staffId"
                            dataSource={config.data as BenchPerson[]}
                            columns={getBenchColumns(config.tableType === "benchFY")}
                            pagination={false}
                            scroll={{ y: 480, x: 760 }}
                        />
                    ) : (
                        <Table<StaffContractAlert>
                            size="small"
                            rowKey="staffId"
                            dataSource={config.data as StaffContractAlert[]}
                            columns={getStaffContractColumns(
                                config.type,
                                config.tableType === "intern"
                                    ? "Intern"
                                    : config.tableType === "trainee"
                                        ? "Trainee"
                                        : undefined
                            )}
                            pagination={false}
                            scroll={{ y: 480, x: 850 }}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
}

function getBenchColumns(isBenchFY = false) {
    return [
        {
            title: "Staff",
            dataIndex: "staffName",
            ellipsis: true,
            render: (value: string) => value || "-",
        },
        {
            title: "Designation",
            dataIndex: "designation",
            ellipsis: true,
            render: (value: string | null) => value || "-",
        },
        {
            title: isBenchFY ? "Bench %" : "Unallocated",
            dataIndex: "benchPercent",
            width: 120,
            align: "right" as const,
            render: (value: number) => (
                <span style={{ color: "var(--error)", display: "block", fontWeight: 600 }}>
                    {value || 0}%
                </span>
            ),
        },
    ];
}

function getStaffContractColumns(
    type: AlertType,
    period?: "Intern" | "Trainee"
) {
    return [
        {
            title: "Staff",
            dataIndex: "staffName",
            width: 180,
            ellipsis: true,
            render: (_: string, row: StaffContractAlert) => (
                <div>
                    <strong>{row.staffName || "-"}</strong>
                    <div className="dashboard-muted-text">ID: {row.employeeId || "-"}</div>
                </div>
            ),
        },
        {
            title: "Client",
            dataIndex: "clientName",
            width: 140,
            ellipsis: true,
            render: (value: string) => value || "-",
        },
        ...(period
            ? [
                {
                    title: `${period} Start`,
                    dataIndex: "startDate",
                    width: 125,
                    render: formatDate,
                },
            ]
            : []),
        {
            title: period ? `${period} End` : "Contract End",
            dataIndex: "contractEndDate",
            width: 125,
            render: formatDate,
        },
        ...(!period && type !== "expired"
            ? [
                {
                    title: "Notice Period",
                    dataIndex: "noticePeriodDays",
                    width: 115,
                    render: (value: number | null) =>
                        value != null ? `${value} days` : "-",
                },
            ]
            : []),
        ...(type === "combined"
            ? [
                {
                    title: "Status",
                    dataIndex: "daysRemaining",
                    width: 110,
                    render: (value: number) => (
                        <Tag color={value < 0 ? "error" : "warning"}>
                            {value < 0 ? "Expired" : "Upcoming"}
                        </Tag>
                    ),
                },
            ]
            : []),
        {
            title:
                type === "combined"
                    ? "Days"
                    : type === "expired"
                        ? "Overdue"
                        : "Days Left",
            dataIndex: "daysRemaining",
            width: 150,
            render: (value: number) => (
                <Tag color={value < 0 ? "error" : "warning"}>
                    {Math.abs(value)}{" "}
                    {type === "combined"
                        ? value < 0
                            ? "days overdue"
                            : "days left"
                        : "days"}
                </Tag>
            ),
        },
    ];
}

function getMonthEntries(): { key: BenchMonthKey; month: string }[] {
    return [
        { key: "previousMonth", month: getPreviousMonthValue() },
        { key: "currentMonth", month: getCurrentMonthValue() },
        { key: "nextMonth", month: getNextMonthValue() },
    ];
}

function getMonthOptions<T extends BenchMonthSummary | BenchMonthFYSummary>(
    months: Record<BenchMonthKey, T>,
    loading: boolean
) {
    // The total is unknown until the month has loaded.
    const total = (month: T) => (loading ? "" : ` - ${month.total}`);
    return [
        {
            label: `Previous Month (${months.previousMonth.label})${total(months.previousMonth)}`,
            value: "previousMonth",
        },
        {
            label: `Current Month (${months.currentMonth.label})${total(months.currentMonth)}`,
            value: "currentMonth",
        },
        {
            label: `Next Month (${months.nextMonth.label})${total(months.nextMonth)}`,
            value: "nextMonth",
        },
    ];
}

function formatDate(value?: string) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function formatMonthValue(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getCurrentMonthValue() {
    return formatMonthValue(new Date());
}

function getPreviousMonthValue() {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return formatMonthValue(date);
}

function getNextMonthValue() {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    return formatMonthValue(date);
}

function getMonthLabel(month: string) {
    const [year, monthValue] = month.split("-");
    const date = new Date(Number(year), Number(monthValue) - 1, 1);
    if (Number.isNaN(date.getTime())) return month;

    return date.toLocaleDateString("en-IN", {
        month: "short",
        year: "numeric",
    });
}

function getEmptyBenchMonth(month: string): BenchMonthSummary {
    return { month, label: getMonthLabel(month), total: 0, people: [] };
}

function getEmptyBenchFYMonth(month: string): BenchMonthFYSummary {
    return { month, label: getMonthLabel(month), total: 0, people: [] };
}
export default StaffDashboardPage;
